/**
 * NOS-128 / NOS-129: Public sermon library (anon, published videos only).
 */
import { useCallback, useEffect, useState } from "react"
import { supabase } from "@/lib/supabase"

const PAGE_SIZE = 12

export interface PublicMediaVideoCard {
  id: string
  title: string
  slug: string
  description: string | null
  thumbnail_url: string | null
  youtube_video_id: string | null
  published_at: string | null
  scripture_passage: string | null
  media_series: { id: string; title: string; slug: string } | null
  media_video_speakers: { speaker_id: string; media_speakers: { id: string; name: string } | null }[] | null
}

export interface PublicMediaFilters {
  seriesSlug: string | null
  speakerId: string | null
  topicSlugs: string[]
  page: number
}

async function videoIdsWithAllTopics(topicIds: string[]): Promise<string[] | null> {
  if (topicIds.length <= 1) return null
  const results = await Promise.all(
    topicIds.map((tid) =>
      supabase.from("media_video_topics").select("video_id").eq("topic_id", tid)
    )
  )
  const per = results.map((r) => (r.data ?? []).map((x: { video_id: string }) => x.video_id))
  if (per.some((rows) => rows.length === 0)) return []
  let acc = new Set(per[0])
  for (let i = 1; i < per.length; i++) {
    const next = new Set(per[i])
    acc = new Set([...acc].filter((id) => next.has(id)))
  }
  return [...acc]
}

export function usePublicMedia(orgId: string | null, filters: PublicMediaFilters) {
  const [videos, setVideos] = useState<PublicMediaVideoCard[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const topicKey = JSON.stringify([...filters.topicSlugs].sort())
  const refresh = useCallback(async () => {
    if (!orgId) {
      setVideos([])
      setTotalCount(0)
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    const now = new Date().toISOString()

    let seriesId: string | null = null
    if (filters.seriesSlug) {
      const { data: ser } = await supabase
        .from("media_series")
        .select("id")
        .eq("org_id", orgId)
        .eq("slug", filters.seriesSlug)
        .maybeSingle()
      seriesId = ser?.id ?? null
      if (!seriesId) {
        setVideos([])
        setTotalCount(0)
        setLoading(false)
        return
      }
    }

    let topicIds: string[] = []
    if (filters.topicSlugs.length > 0) {
      const { data: tops } = await supabase
        .from("media_topics")
        .select("id, slug")
        .eq("org_id", orgId)
        .in("slug", filters.topicSlugs)
      topicIds = (tops ?? []).map((t) => t.id)
      if (topicIds.length === 0) {
        setVideos([])
        setTotalCount(0)
        setLoading(false)
        return
      }
    }

    const topicAndIds = await videoIdsWithAllTopics(topicIds)
    if (topicAndIds && topicAndIds.length === 0) {
      setVideos([])
      setTotalCount(0)
      setLoading(false)
      return
    }

    const speakerFilter = filters.speakerId
    const spInner = Boolean(speakerFilter)
    const tpInner = topicIds.length === 1

    const speakersSelect = spInner
      ? "media_video_speakers!inner(speaker_id, media_speakers ( id, name ))"
      : "media_video_speakers ( speaker_id, media_speakers ( id, name ) )"
    const topicsSelect = tpInner
      ? "media_video_topics!inner(topic_id)"
      : "media_video_topics ( topic_id )"

    let q = supabase
      .from("media_videos")
      .select(
        `id, title, slug, description, thumbnail_url, youtube_video_id, published_at, scripture_passage,
        media_series ( id, title, slug ),
        ${speakersSelect},
        ${topicsSelect}`,
        { count: "exact" }
      )
      .eq("org_id", orgId)
      .eq("is_published", true)
      .or(`published_at.is.null,published_at.lte.${now}`)
      .order("published_at", { ascending: false, nullsFirst: false })

    if (seriesId) {
      q = q.eq("series_id", seriesId)
    }
    if (speakerFilter) {
      q = q.eq("media_video_speakers.speaker_id", speakerFilter)
    }
    if (topicIds.length === 1) {
      q = q.eq("media_video_topics.topic_id", topicIds[0])
    }
    if (topicAndIds && topicAndIds.length > 0) {
      q = q.in("id", topicAndIds)
    }

    const from = (Math.max(1, filters.page) - 1) * PAGE_SIZE
    const { data, error: fetchError, count } = await q.range(from, from + PAGE_SIZE - 1)

    if (fetchError) {
      setError(fetchError.message)
      setVideos([])
      setTotalCount(0)
    } else {
      setVideos((data ?? []) as PublicMediaVideoCard[])
      setTotalCount(count ?? 0)
    }
    setLoading(false)
  }, [orgId, filters.seriesSlug, filters.speakerId, topicKey, filters.page])

  useEffect(() => {
    refresh()
  }, [refresh])

  return { videos, totalCount, loading, error, pageSize: PAGE_SIZE, refresh }
}

export function usePublicMediaTaxonomies(orgId: string | null) {
  const [series, setSeries] = useState<Array<{ id: string; title: string; slug: string }>>([])
  const [speakers, setSpeakers] = useState<Array<{ id: string; name: string }>>([])
  const [topics, setTopics] = useState<Array<{ id: string; name: string; slug: string }>>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!orgId) {
      setSeries([])
      setSpeakers([])
      setTopics([])
      setLoading(false)
      return
    }
    let cancelled = false
    ;(async () => {
      const [sRes, spRes, tRes] = await Promise.all([
        supabase.from("media_series").select("id, title, slug").eq("org_id", orgId).order("title"),
        supabase.from("media_speakers").select("id, name").eq("org_id", orgId).order("name"),
        supabase.from("media_topics").select("id, name, slug").eq("org_id", orgId).order("name"),
      ])
      if (cancelled) return
      setSeries((sRes.data ?? []) as typeof series)
      setSpeakers((spRes.data ?? []) as typeof speakers)
      setTopics((tRes.data ?? []) as typeof topics)
      setLoading(false)
    })()
    return () => {
      cancelled = true
    }
  }, [orgId])

  return { series, speakers, topics, loading }
}

export interface PublicMediaVideoDetail {
  id: string
  org_id: string
  title: string
  slug: string
  description: string | null
  youtube_video_id: string | null
  thumbnail_url: string | null
  audio_url: string | null
  published_at: string | null
  scripture_passage: string | null
  transcript: string | null
  ai_study_guide: string | null
  series_id: string | null
  media_series: { id: string; title: string; slug: string } | null
  media_video_speakers: {
    speaker_id: string
    media_speakers: { id: string; name: string } | null
  }[] | null
  media_video_topics: {
    topic_id: string
    media_topics: { id: string; name: string; slug: string } | null
  }[] | null
}

export function usePublicMediaVideo(orgId: string | null, videoSlug: string | null) {
  const [video, setVideo] = useState<PublicMediaVideoDetail | null>(null)
  const [moreInSeries, setMoreInSeries] = useState<PublicMediaVideoCard[]>([])
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    if (!orgId || !videoSlug) {
      setVideo(null)
      setMoreInSeries([])
      setNotFound(!videoSlug)
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    setVideo(null)
    setMoreInSeries([])
    const { data, error: fetchError } = await supabase
      .from("media_videos")
      .select(
        `id, org_id, title, slug, description, youtube_video_id, thumbnail_url, audio_url, published_at, scripture_passage, transcript, ai_study_guide, series_id,
        media_series ( id, title, slug ),
        media_video_speakers ( speaker_id, media_speakers ( id, name ) ),
        media_video_topics ( topic_id, media_topics ( id, name, slug ) )`
      )
      .eq("org_id", orgId)
      .eq("slug", videoSlug)
      .maybeSingle()

    if (fetchError) {
      setError(fetchError.message)
      setVideo(null)
      setMoreInSeries([])
      setNotFound(true)
      setLoading(false)
      return
    }
    if (!data) {
      setVideo(null)
      setMoreInSeries([])
      setNotFound(true)
      setLoading(false)
      return
    }

    const row = data as unknown as PublicMediaVideoDetail
    setVideo(row)
    setNotFound(false)

    const sid = row.series_id
    if (sid) {
      const now = new Date().toISOString()
      const { data: sibs } = await supabase
        .from("media_videos")
        .select(
          `id, title, slug, description, thumbnail_url, youtube_video_id, published_at, scripture_passage,
          media_series ( id, title, slug ),
          media_video_speakers ( speaker_id, media_speakers ( id, name ) )`
        )
        .eq("org_id", orgId)
        .eq("series_id", sid)
        .eq("is_published", true)
        .or(`published_at.is.null,published_at.lte.${now}`)
        .neq("id", row.id)
        .order("published_at", { ascending: false, nullsFirst: false })
        .limit(6)
      setMoreInSeries((sibs ?? []) as PublicMediaVideoCard[])
    } else {
      setMoreInSeries([])
    }
    setLoading(false)
  }, [orgId, videoSlug])

  useEffect(() => {
    refresh()
  }, [refresh])

  return { video, moreInSeries, loading, notFound, error, refresh }
}
